import React, { useMemo, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { Check, Close, MoreVert, RemoveCircleOutlined, Search } from '@mui/icons-material';
import {
  useGetOrgGatesQuery,
  useSetOrgStatsigGateOverrideMutation,
  useRemoveOrgStatsigGateOverrideMutation,
} from '../store/api';
import type { OrgGateStatusResponse } from '@store/generated/admin';
import { StatsigGateRow } from './statsig-gate-row';

interface OrganizationStatsigGatesSectionProps {
  organizationId: string;
}

export function OrganizationStatsigGatesSection({
  organizationId,
}: OrganizationStatsigGatesSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: orgGates, isLoading } = useGetOrgGatesQuery(
    { organizationId },
    { skip: !organizationId },
  );

  const gates = orgGates?.gates ?? [];

  const filteredGates = useMemo(() => {
    if (!searchQuery) return gates;
    const query = searchQuery.toLowerCase();
    return gates.filter(
      gate =>
        gate.name.toLowerCase().includes(query) ||
        gate.gate_id.toLowerCase().includes(query) ||
        gate.description?.toLowerCase().includes(query),
    );
  }, [gates, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!gates.length) {
    return (
      <div className="p-4 text-gray-500 text-sm">
        No organization-scoped feature gates available
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        Feature gates targeted at organizations. Force a gate to pass or fail for this
        organization; changes apply to everyone in the org.
      </p>

      <div className="mb-4">
        <TextField
          size="small"
          placeholder="Search gates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" className="text-gray-400" />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>

      <div className="space-y-1">
        {filteredGates.map(gate => (
          <OrgGateRow key={gate.gate_id} gate={gate} organizationId={organizationId} />
        ))}
      </div>

      {filteredGates.length === 0 && (
        <div className="p-4 text-gray-500 text-sm text-center">
          No gates match &quot;{searchQuery}&quot;
        </div>
      )}
    </div>
  );
}

interface OrgGateRowProps {
  gate: OrgGateStatusResponse;
  organizationId: string;
}

function OrgGateRow({ gate, organizationId }: OrgGateRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [setOverride, { isLoading: isSettingOverride }] =
    useSetOrgStatsigGateOverrideMutation();
  const [removeOverride, { isLoading: isRemovingOverride }] =
    useRemoveOrgStatsigGateOverrideMutation();

  const isUpdating = isSettingOverride || isRemovingOverride;

  // The backend route uses the URL org id for the target of the override and
  // applies the state atomically (adds to one dimension, removes from the
  // other), so we just declare the desired state here.
  const handleSetOverride = async (value: 'pass' | 'fail') => {
    setMenuAnchor(null);
    try {
      await setOverride({
        organizationId,
        gateId: gate.gate_id,
        setOrgGateOverrideRequest: { state: value },
      }).unwrap();
    } catch (error) {
      console.error('Failed to set org gate override:', error);
    }
  };

  const handleRemoveOverride = async () => {
    setMenuAnchor(null);
    try {
      await removeOverride({
        organizationId,
        gateId: gate.gate_id,
      }).unwrap();
    } catch (error) {
      console.error('Failed to remove org gate override:', error);
    }
  };

  return (
    <StatsigGateRow
      name={gate.name}
      description={gate.description}
      isEnabled={gate.is_enabled}
      evaluatedResult={gate.evaluated_result}
      overrideStatus={gate.override_status}
      actions={
        <>
          <IconButton
            size="small"
            onClick={e => setMenuAnchor(e.currentTarget)}
            disabled={isUpdating}
            aria-label="Organization gate override actions"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleSetOverride('pass')} disabled={isUpdating}>
              <Check className="mr-2 text-green-600" fontSize="small" />
              Force Pass
            </MenuItem>
            <MenuItem onClick={() => handleSetOverride('fail')} disabled={isUpdating}>
              <Close className="mr-2 text-red-600" fontSize="small" />
              Force Fail
            </MenuItem>
            {gate.override_status && (
              <MenuItem onClick={handleRemoveOverride} disabled={isUpdating}>
                <RemoveCircleOutlined className="mr-2 text-gray-600" fontSize="small" />
                Remove Override
              </MenuItem>
            )}
          </Menu>
        </>
      }
    />
  );
}
